import { Eye, EyeClosed } from "@phosphor-icons/react";
import { Button, Grid, Text, TextField } from "@radix-ui/themes";
/* eslint-disable-next-line import/named */
import { Link as RouterLink, useSearch } from "@tanstack/react-router";
import cx from "classnames";
import { useReducer, type FormEventHandler } from "react";

import { forgotPasswordRoute } from "../forgot-password/route";

interface LoginFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  isValid: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  checkValidity: () => void;
  loading: boolean;
  disabled: boolean;
  error: boolean;
  errorMessage?: string;
  "data-testid"?: string;
}

export function LoginForm({
  formRef,
  isValid,
  onSubmit,
  checkValidity,
  loading,
  disabled,
  error,
  errorMessage,
  "data-testid": dataTestId = "login-form",
}: LoginFormProps) {
  const [isPasswordVisible, togglePasswordVisibility] = useReducer(
    (x) => !x,
    false,
  );
  const search = useSearch({ strict: false });

  return (
    <>
      <Text
        weight="medium"
        className="mb-2 hidden text-2xl tracking-tight sm:block"
        data-testid="login-description"
      >
        Log in to your account.
      </Text>

      <form
        onSubmit={onSubmit}
        className="grid gap-4 sm:gap-6"
        ref={formRef}
        data-testid={dataTestId}
        aria-label="Log in"
      >
        <Grid gap={{ initial: "1", sm: "3" }}>
          <Text weight="medium" data-testid="email-field-label">
            Work Email
          </Text>
          <TextField.Root
            required
            variant="classic"
            name="email"
            id="email-input"
            placeholder="name@work-email.com"
            type="email"
            autoComplete="true"
            disabled={disabled}
            onInput={checkValidity}
            autoFocus
            size="2"
            data-testid="email-field"
          />
          <Text
            color="red"
            size="2"
            data-testid="email-error-message"
            className={cx({ hidden: !error })}
          >
            Invalid or unrecognized email address
          </Text>
        </Grid>

        <Grid gap="2">
          <Grid gap={{ initial: "1", sm: "3" }}>
            <Text weight="medium" data-testid="password-field-label">
              Password
            </Text>
            <TextField.Root
              required
              variant="classic"
              onInput={checkValidity}
              name="password"
              id="password-input"
              placeholder="Enter your password"
              type={isPasswordVisible ? "text" : "password"}
              autoComplete="true"
              disabled={disabled}
              data-testid="password-field"
            >
              <TextField.Slot
                side="right"
                className="px-3"
                onClick={togglePasswordVisibility}
              >
                {isPasswordVisible ? (
                  <EyeClosed size={16} />
                ) : (
                  <Eye size={16} />
                )}
              </TextField.Slot>
            </TextField.Root>
            <Text
              color="red"
              size="2"
              data-testid="password-error-message"
              className={cx({ hidden: !error })}
            >
              {errorMessage || "Invalid email or password combination"}
            </Text>
          </Grid>

          <RouterLink
            to={forgotPasswordRoute.to}
            search={search.redirect ? { redirect: search.redirect } : undefined}
            data-testid="forgot-password-link"
            className="puntt-link w-max underline decoration-puntt-accent-5 underline-offset-4"
          >
            <Text size="2">Forgot Password</Text>
          </RouterLink>
        </Grid>

        <Button
          type="submit"
          disabled={!isValid || disabled}
          loading={loading}
          className="mt-2 w-full py-3 font-medium"
          data-testid="form-submit-cta"
        >
          Log In
        </Button>
      </form>
    </>
  );
}
